import React, { useMemo, useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import styled, { createGlobalStyle } from "styled-components";
import { Scrollbar } from "react-scrollbars-custom";

const SidebarGlobalStyles = createGlobalStyle`
    body.hide-subject-sidebar .re-registration-detail-lft.pmc-reregistration-sidebar .close-btn-sidebar {
        position: absolute;
        right: -45px;
        top: 25px;
        display: flex;
        z-index: 10;
    }

    @media only screen and (max-width: 1024px) {
        body.hide-subject-sidebar .re-registration-detail-lft.pmc-reregistration-sidebar .close-btn-sidebar {
            left: auto!important;
            top: 22px!important;
            right: 0!important;
            z-index: 999;
        }
    }
`;

// PMC step mapping matching Figma design:
// Step 1: Review Module Selection - Review and confirm modules for upcoming semester
// Step 2: Review Account Details - Review account details and submit re-registration
// Step 5: Upload Outstanding Documents (when applicable)
const stepsMap = {
    1: { label: "Review Module Selection", description: "Review and confirm your modules for the upcoming semester." },
    2: { label: "Review Account Details", description: "Review your account details and submit your re-registration." },
    5: { label: "Upload Outstanding Documents", description: "Upload any required supporting documents." }
};

const SidebarContainer = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    background-color: var(--white);
    box-shadow: 1px 0px 10px rgba(204, 204, 204, 0.68);
    padding: 20px;
    width: 320px;
    height: 100%;
    max-height: 100vh;
    box-sizing: border-box;
    z-index: 101;
`;

const SidebarScrollArea = styled.div`
    flex: 1 1 auto;
    min-height: 0;
`;

const SidebarHeader = styled.div`
    display: flex;
    flex-direction: column;
`;

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 0.75rem;
`;

const BackLink = styled(Link)`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 16px;
    color: var(--base-color-link, #2b7dd4);
    text-decoration: none;
`;

const IconButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    border: none;
    background: transparent;
    cursor: pointer;
    padding: 0;
`;

const StatusBadge = styled.div`
    display: inline-block;
    padding: 3px 5px;
    border-radius: 2px;
    font-size: 10px;
    font-weight: 600;
    background: rgba(92, 184, 92, 0.25);
    color: #5cb85c;
    align-self: flex-start;
    margin-bottom: 0.5rem;
`;

const Title = styled.h3`
    font-size: 16px;
    font-weight: 700;
    color: var(--black1);
    margin: 0 0 0.25rem 0;
`;

const SchoolName = styled.div`
    font-weight: 600;
    color: var(--lightGray3);
    margin-bottom: 0.5rem;
`;

const IntakeRow = styled.div`
    display: flex;
    gap: 1rem;
    font-size: 13px;
    color: var(--lightGray3);
`;

const IntakeItem = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
`;

const Divider = styled.hr`
    border: none;
    border-top: 1px solid var(--lightBlue);
    margin: 14px 0;
`;

const InfoRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 14px;
    gap: 12px;
`;

const InfoLabel = styled.span`
    font-weight: 600;
`;

const InfoValue = styled.span`
    display: flex;
    align-items: center;
    gap: 0.3rem;
`;

const StatusText = styled.span`
    &.red {
        color: var(--red, #c1232c);
    }
    &.green {
        color: var(--green, #5cb85c);
    }
    &.orange {
        color: var(--orange, #ff9800);
    }
`;

const StepsContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    
`;

const StepRow = styled.div`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    gap: 1rem;
    cursor: ${props => props.$clickable ? 'pointer' : 'default'};

    ${props => props.$clickable && props.$stepVariant && `
        &:hover > div:first-child {
            background-color: ${props.$stepVariant === "done" ? "#4cae4c" : "var(--button-hover-color, #1a3a5c)"};
        }
    `}

    &::after {
        content: "";
        position: absolute;
        left: 18px;
        top: 36px;
        width: 1px;
        height: calc(100% + 1rem);
        background: var(--darkBlue, #06172a);
    }

    &:last-child::after {
        display: none;
    }
`;

const StepIcon = styled.div`
    z-index: 1;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 16px;
    line-height: 1;
    font-weight: 600;
    flex: 0 0 auto;
    color: var(--white, var(--white));
    background-color: ${props => {
        if (props.$variant === "done") return "#5cb85c";
        if (props.$variant === "active") return "var(--button-active-color, #06172a)";
        return "var(--scrollColor)";
    }};
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
        background-color: ${props => props.$variant === "done" ? "#4cae4c" : "var(--button-hover-color, #1a3a5c)"};
    }
`;

const StepContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

const StepTitle = styled.div`
    font-size: 14px;
    font-weight: 600;
    line-height: 14px;
    color: var(--black1);
`;

const StepDescription = styled.div`
    font-size: 12px;
    color: var(--lightGray3);
`;

const IntroText = styled.p`
    font-size: 12px;
    color: var(--lightGray3);
    text-box-edge: cap alphabetic;
  text-box-trim: trim-both;
    margin: 0 0 16px 0;
`;

const PrimaryButton = styled.button`
    background-color: var(--button-color, #2b7dd4);
    border: 1px solid var(--button-color, #2b7dd4);
    color: var(--white);
    padding: 8px 12px;
    border-radius: 2px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    width: 100%;
    min-height: 34px;
    
    &:hover {
        background-color: var(--button-hover-color, #1f5fa6);
        border-color: var(--button-hover-color, #1f5fa6);
    }
`;

const ModuleSummaryContainer = styled.div`
`;

const ModuleSummaryHeader = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const ModuleSummaryTopRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    width: 100%;
`;

const ModuleSummaryTitleGroup = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const ModuleSummaryTitle = styled.div`
    font-weight: 600;
    color: var(--black1);
    font-size: 12px;
`;

const ModuleSummaryMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 10px;
    color: var(--lightGray3);
`;

const ModuleSummaryAmount = styled.div`
    font-size: 12px;
    font-weight: 700;
    color: var(--black1);
`;

const ProgressTrackWrapper = styled.div`
    margin-top: 5px; /* Matched QualificationHeadBox spacing */
`;

const ProgressTrackBackground = styled.div`
    width: 100%;
    height: 15px;
    border-radius: 3px;
    background-color: #edebe9;
    overflow: hidden;
`;

const ProgressTrackFill = styled.div`
    height: 15px;
    border-radius: 3px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 8px;
    box-sizing: border-box;
    background-image: ${props => {
        if (props.$status === "none") {
            return "linear-gradient(90deg, var(--scrollColor) 0%, var(--scrollColor) 100%)";
        }
        if (props.$status === "error") {
            return "linear-gradient(90deg, #c1232c 0%, #ff6b6b 100%)";
        }
        // Default/OK state – no yellow warning styling
        return "linear-gradient(90deg, #467fcf 0%, #79b3ff 199.29%)";
    }};
    width: ${(props) => props.$percent}%;
`;

const ProgressTrackLabel = styled.div`
    font-size: 10px;
    font-weight: 600;
    padding: 0 2px;
    text-align: center!important;
    width: 100%;
    color: var(--white);
    white-space: nowrap;
`;

const ProgressTrackHelper = styled.div`
    margin-top: 3px;
    font-size: 10px;
    font-weight: 600;
    color: var(--lightGray3);
`;

const ProgressToggleRow = styled.button`
    width: 100%;
    padding: 8px 0 0;
    display: flex;
    justify-content: space-between;
    width: 124px;
    align-items: center;
    gap: 5px;
    border: none;
    background: transparent;
    font-size: 10px;
    color: #000000;
    cursor: pointer;
`;

const ModuleItemsWrapper = styled.div`
    /* Dynamic values are controlled via CSS custom properties that are
       updated from layout measurements in the component logic. */
    --module-items-max-height: 300px;
    --module-items-scrollbar-gap: 0px;

    max-height: var(--module-items-max-height);
    height: auto;
    width: calc(100% + var(--module-items-scrollbar-gap));
    overflow-y: auto;

    scrollbar-width: thin;
    scrollbar-color: var(--scrollbar-thumb-color, var(--scrollColor)) transparent;

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-thumb {
        background-color: var(--scrollbar-thumb-color, var(--scrollColor));
        border-radius: 999px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }
`;

const ModuleItemsContainer = styled.div`
    padding: 8px 0 12px 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    min-height: 0;
    box-sizing: border-box;
`;

const ModuleItemRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 12px;
    gap: 12px;
`;

const ModuleItemName = styled.div`
    color: #1d1d1d;
`;

const ModuleItemPrice = styled.div`
    font-size: 12px;
    font-weight: 700;
    color: ${(props) => (props.$isCredit ? "#5cb85c" : "#1d1d1d")};
    white-space: nowrap;
`;

const SummarySection = styled.div`
    display: flex;
    flex-direction: column;
    padding: 4px 0 ;
    gap: 12px;
`;

const SidebarFooterPinned = styled.div`
    position: sticky;
    bottom: 0;
    left: 0;
    right: 0;
    flex: 0 0 auto;
    background-color:rgb(255, 255, 255);
    border-top: 1px solid #d7dee8;
    padding: 12px 0 0 0;
    z-index: 2;
`;

const SummaryRow = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    font-size: 14px;
`;

const SummaryRowTitle = styled.div`
    font-weight: 400;
      text-box-edge: cap alphabetic;
  text-box-trim: trim-both;
    color: var(--black1);
`;

const SummaryRowAmount = styled.div`
    font-size: 12px;
    font-weight: 700;
      text-box-edge: cap alphabetic;
  text-box-trim: trim-both;
    color: ${(props) => props.$color || "var(--black1)"};
`;

const SummaryHelperText = styled.p`
    margin: 8px 0 0 0;
    font-size: 10px;
      text-box-edge: cap alphabetic;
  text-box-trim: trim-both;
    text-wrap: pretty;
    line-height: 1.1;
    color: ${(props) => props.$color || "var(--gray1)"};
`;

const SummaryTotalRow = styled.div`
    margin-top: 12px;
    padding-top: 14px;
    padding-bottom: 4px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 16px;
    border-top: 1px solid #d7dee8;
`;

const SummaryTotalLabel = styled.span`
    color: var(--black1);
    font-size: 16px;
    font-weight: 400;
    line-height: 1; 
`;

const SummaryTotalAmount = styled.span`
    font-weight: 700;
    font-size: 20px;
    display: flex;
    align-items: flex-start;
    gap: 2px;
    color: var(--black1);
`;

const SummaryDisclaimer = styled.div`
    display: flex;
    padding:0 0 14px 0;
    align-items: baseline;
    gap: 4px;
       font-size: 10px;
    text-wrap: pretty;
    line-height: 1.1;
    color: var(--error-color);
    font-weight: 400;
    font-family: "Segoe UI";
    unicode-bidi: isolate;
`;
const SummaryDisclaimerText = styled.span`
   
    color: var(--lightGray3);
`;

const TotalAsterisk = styled.span`
    font-size: 10px;
    color: var(--error-color);
    font-weight: 700;
`;

const formatCurrency = (amount) => {
    const numericAmount = Number(amount) || 0;
    return `R ${numericAmount.toLocaleString("en-ZA", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    })}`;
};

function PMCReRegistrationSidebar(props) {
    const {
        registration,
        studentStatus,
        currentStep,
        stepsAvailable,
        submitted,
        activeTab,
        onStartReRegistration,
        moduleSummary,
        onStepClick,
        onTabChange,
        readOnlyData
    } = props;

    
    const statusValue = (readOnlyData?.status ?? registration?.status ?? "").trim().toLowerCase();
    const accountPayerUpdatedValue = Number(registration?.account_payer_updated || 0);

    const isStep1Complete = statusValue === "submitted" || Boolean(submitted);
    const isStep2Complete = accountPayerUpdatedValue === 1;

    const [isModuleSummaryExpanded, setIsModuleSummaryExpanded] = useState(false);

    const sidebarContainerRef = useRef(null);
    const sidebarHeaderContentRef = useRef(null);
    const moduleItemsWrapperRef = useRef(null);
    const sidebarFooterRef = useRef(null);

    const isOverviewTab = activeTab === "overview";

    // Set isModuleSummaryExpanded to true if tab is not "application" or step is not 1
    useEffect(() => {
        if (activeTab !== "application" || currentStep !== 1) {
            setIsModuleSummaryExpanded(true);
        } else {
            setIsModuleSummaryExpanded(false);
        }
    }, [activeTab, currentStep]);

    const handleSideBar = () => {
        const body = document.body;
        body.classList.toggle("hide-subject-sidebar");
        if (body.classList.contains("hide-subject-sidebar")) {
            body.classList.remove("sub-sidebar-c");
        } else {
            body.classList.add("sub-sidebar-c");
        }
    };
    
    const steps = useMemo(() => {
        const effectiveCurrentStep = currentStep === 5 ? 2 : currentStep;
        
        const validSteps = stepsAvailable
            .filter(stepId => stepId !== 4) // Remove step 4 if present
            .filter(stepId => stepsMap[stepId] && stepId !== 5); // Exclude step 5 from stepper
        
        const hasStep1 = validSteps.includes(1);
        const hasStep2 = validSteps.includes(2);
        
        let finalSteps = validSteps;
        if (!hasStep1 || !hasStep2) {
            finalSteps = [1, 2].filter(step => !finalSteps.includes(step)).concat(finalSteps).sort((a, b) => a - b);
        }
        
        const stepperSteps = finalSteps
            .map((stepId, idx) => {
                return {
                    ...stepsMap[stepId],
                    id: stepId,
                    displayIndex: idx + 1,
                    isActive: effectiveCurrentStep === stepId
                };
            });
        return stepperSteps;
    }, [stepsAvailable, currentStep]);

    const hasCompletedStep = useMemo(() => {
        // At least one step is completed
        return isStep1Complete || isStep2Complete;
    }, [isStep1Complete, isStep2Complete]);

    const areAllStepsComplete = useMemo(() => {
        return isStep1Complete && isStep2Complete;
    }, [isStep1Complete, isStep2Complete]);

    const areStepsVisibleOnOverview = useMemo(() => {
        return isOverviewTab && hasCompletedStep;
    }, [isOverviewTab, hasCompletedStep]);

    const getRegistrationStatus = () => {
        // Use status and statusColor directly from reRegistrationList
        const status = registration?.status;
        const statusColor = registration?.statusColor;
        
        // Determine icon based on status text or statusColor
        let iconClass = "fal fa-user-check";
        if (status && (status.toLowerCase().includes("outstanding") || status.toLowerCase().includes("required"))) {
            iconClass = "fal fa-exclamation-circle";
        } else if (statusColor === "red" || (status && (status.toLowerCase().includes("not submitted") || status.toLowerCase().includes("declined")))) {
            iconClass = "fal fa-user-times";
        }
        
        // Use status from reRegistrationList if available, otherwise use fallbacks
        const displayStatus = status || (submitted ? "Submitted" : (registration?.per || studentStatus ? "Submitted" : "Re-Registration Not Submitted"));
        const displayColor = statusColor || (submitted ? "green" : (registration?.per || studentStatus ? "green" : "red"));
        
        return (
            <StatusText className={displayColor}>
                <i className={iconClass}></i> {displayStatus}
            </StatusText>
        );
    };

    const renderStep = ({ id, description, displayIndex, label, isActive }) => {
        let isDone = false;
        if (id === 1) {
            isDone = isStep1Complete;
        } else if (id === 2) {
            isDone = isStep2Complete;
        }
        
        const isClickable = onStepClick && (isDone || stepsAvailable.includes(id));
        const stepVariant = isDone ? "done" : (isActive ? "active" : "pending");
        
        const handleStepClick = () => {
            if (isClickable && onStepClick) {
                onStepClick(id);
                if (onTabChange && (id === 1 || id === 2)) {
                    onTabChange("application");
                }
            }
        };
        
        return (
            <StepRow 
                key={id} 
                $clickable={isClickable}
                $stepVariant={stepVariant}
                onClick={handleStepClick}
            >
                {isDone ? (
                    <StepIcon $variant="done">
                        <i className="fal fa-check"></i>
                    </StepIcon>
                ) : isActive ? (
                    <StepIcon $variant="active">
                        {displayIndex}
                    </StepIcon>
                ) : (
                    <StepIcon $variant="pending">
                        {displayIndex}
                    </StepIcon>
                )}
                <StepContent>
                    <StepTitle>{label}</StepTitle>
                    <StepDescription>{description}</StepDescription>
                </StepContent>
            </StepRow>
        );
    };

    const {
        items = [],
        modulesCountSelected = 0,
        modulesRequiredMin = 0,
        modulesAllowedMax = 0,
        modulesTotal = 0,
        adjustmentsTotal = 0,
        depositAmount: apiDepositAmount = 0,
        totalAmount: apiTotalAmount = 0,
        deliveryMethodLabel,
        qualificationName
    } = moduleSummary || {};

    // Get student number from localStorage (same as PaymentMethods component)
    const userData = useMemo(() => {
        try {
            return JSON.parse(localStorage.getItem('user')) || {};
        } catch {
            return {};
        }
    }, []);
    const studentNumber = userData?.student_crm_id || "";



    // Total is Module cost less adjustments
    const calculatedTotal = useMemo(() => {
        if (submitted && apiTotalAmount > 0) {
            return Number(apiTotalAmount);
        }
        return Math.max(0, (Number(modulesTotal) || 0) - (Number(adjustmentsTotal) || 0));
    }, [modulesTotal, adjustmentsTotal, submitted, apiTotalAmount]);
    
    // Calculate deposit based on final total AFTER adjustments
    // In submitted/read-only mode, use API deposit amount
    const calculatedDeposit = useMemo(() => {
        if (submitted && apiDepositAmount > 0) {
            return Number(apiDepositAmount);
        }
        // Otherwise calculate based on final total
        const finalTotal = calculatedTotal;
        if (finalTotal <= 0) {
            return 0;
        }

        if (finalTotal >= 14000) {
            return 7000;
        }
        return Number((finalTotal * 0.5).toFixed(2));
    }, [calculatedTotal, submitted, apiDepositAmount]);

    const progressPercent = (() => {
        // Treat reaching the minimum as 100% "ready", and max as an upper guard-rail.
        // If a minimum is configured, we base progress on that; otherwise we fall back to max.
        const denominator = modulesRequiredMin > 0
            ? modulesRequiredMin
            : modulesAllowedMax > 0
                ? modulesAllowedMax
                : 0;

        if (!denominator || denominator <= 0) {
            return 0;
        }

        const ratio = modulesCountSelected / denominator;
        const clamped = Math.max(0, Math.min(1, ratio));
        const logicalPercent = clamped * 100;

        // Ensure there is always enough bar width to comfortably fit the label text.
        // Use a slightly smaller minimum when nothing is selected so the neutral bar
        // does not dominate the UI.
        const minPercent = modulesCountSelected === 0 ? 30 : 35;

        return Math.max(minPercent, logicalPercent);
    })();

    const isAboveMax = modulesAllowedMax > 0 && modulesCountSelected > modulesAllowedMax;

    const maxDisplayModules = modulesAllowedMax > 0
        ? modulesAllowedMax
        : modulesRequiredMin > 0
            ? modulesRequiredMin
            : 0;

    const progressStatus = modulesCountSelected === 0
        ? "none"
        : isAboveMax
            ? "error"
            : "ok";

    const hasModuleSummary = Boolean(moduleSummary);

    useEffect(() => {
        const sidebarElement = sidebarContainerRef.current;
        const modulesElement = moduleItemsWrapperRef.current;
        const footerElement = sidebarFooterRef.current;

        if (!sidebarElement || !modulesElement) {
            return;
        }

        let animationFrameId = null;
        let resizeObserverInstance = null;

        const computeAndApplyLayout = () => {
            if (!modulesElement) {
                return;
            }

            const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
            const modulesRect = modulesElement.getBoundingClientRect();

            const bottomSafePadding = 10;
            const minContentHeight = 30;

            let availableHeight;

            if (footerElement) {
                const footerRect = footerElement.getBoundingClientRect();
                // When the pinned footer is present, stop the scrollable area
                // above the footer so that content never hides behind it.
                availableHeight = footerRect.top - modulesRect.top - bottomSafePadding;
            } else {
                // Fallback to viewport height when there is no pinned footer.
                availableHeight = viewportHeight - modulesRect.top - bottomSafePadding;
            }

            const resolvedMaxHeight = Math.max(minContentHeight, availableHeight);

            modulesElement.style.setProperty("--module-items-max-height", `${resolvedMaxHeight}px`);

            const hasVerticalScrollbar = modulesElement.scrollHeight > modulesElement.clientHeight + 1;
            modulesElement.style.setProperty(
                "--module-items-scrollbar-gap",
                hasVerticalScrollbar ? "10px" : "0px"
            );
        };

        const scheduleLayoutComputation = () => {
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId);
            }
            animationFrameId = window.requestAnimationFrame(computeAndApplyLayout);
        };

        scheduleLayoutComputation();

        if (typeof ResizeObserver !== "undefined") {
            resizeObserverInstance = new ResizeObserver(scheduleLayoutComputation);
            resizeObserverInstance.observe(document.documentElement);
            resizeObserverInstance.observe(sidebarElement);
            if (footerElement) {
                resizeObserverInstance.observe(footerElement);
            }
        } else {
            window.addEventListener("resize", scheduleLayoutComputation);
            window.addEventListener("orientationchange", scheduleLayoutComputation);
        }

        return () => {
            if (animationFrameId !== null) {
                cancelAnimationFrame(animationFrameId);
            }
            if (resizeObserverInstance) {
                resizeObserverInstance.disconnect();
            }
            window.removeEventListener("resize", scheduleLayoutComputation);
            window.removeEventListener("orientationchange", scheduleLayoutComputation);
        };
    }, [items.length, isModuleSummaryExpanded]);

    return (
        <>
            <SidebarGlobalStyles />
            <SidebarContainer
                className="re-registration-detail-lft pmc-reregistration-sidebar pmc-reregistration-lft"
                ref={sidebarContainerRef}
            >
            <SidebarScrollArea>
                <SidebarHeader ref={sidebarHeaderContentRef}>
                    <HeaderRow>
                        
                        <BackLink to="/reregistration">
                            <i className="fal fa-chevron-left"></i>
                            <span>Back</span>
                        </BackLink>
                        <button
                            className="close-btn-sidebar"
                            onClick={handleSideBar}
                            title="Close Side Bar"
                        >
                            <i className="close-bar close-bar-1"></i>
                            <i className="close-bar close-bar-2"></i>
                            <i className="close-bar close-bar-3"></i>
                        </button>
                    </HeaderRow>
                    <StatusBadge>IN PROGRESS</StatusBadge>
                    <Title>
                        {qualificationName || `Re-Registration, Intake ${registration?.intake_number} ${registration?.intake_year}`}
                    </Title>
                    <SchoolName>{registration?.academy2}</SchoolName>
                    <IntakeRow>
                        <IntakeItem>
                            <i className="fal fa-calendar-clock" style={{ color: "var(--topbar-color)" }}></i>
                            <span>
                                Intake {registration?.intake_number}, {registration?.intake_year}
                            </span>
                        </IntakeItem>
                        <IntakeItem>
                            <i className="fal fa-calendar-alt" style={{ color: "var(--topbar-color)" }}></i>
                            <span>
                                {registration?.start_date} to {registration?.end_date}
                            </span>
                        </IntakeItem>
                    </IntakeRow>
                    <Divider />
                <div style={{display: "flex", gap: "8px", flexDirection: "column"}}>

              
                    <InfoRow>
                        <InfoLabel>Your Status:</InfoLabel>
                        <InfoValue>{getRegistrationStatus()}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                        <InfoLabel>Close Date:</InfoLabel>
                        <InfoValue>
                            <i className="fal fa-calendar-alt" style={{ color: "var(--topbar-color)" }}></i>
                            <span>{registration?.end_date}</span>
                        </InfoValue>
                    </InfoRow>
                    </div>
                    <Divider />
                    {/* Always show steps when not on overview tab, or when steps are completed/submitted */}
                    {/* Steps should always be visible except on overview tab when no steps are completed */}
                    {(!isOverviewTab || hasCompletedStep || submitted) && steps.length > 0 && (
                        <StepsContainer>
                            {steps.map((step) => renderStep(step))}
                        </StepsContainer>
                    )}
                    {isOverviewTab && (
                        <>
                            {onStartReRegistration && !submitted && (
                                <>
                                    {!areStepsVisibleOnOverview && (
                                        <IntroText>
                                            This is a summary of every module in your programme. Navigate to the{" "}
                                            <strong>Application tab</strong> to choose your modules for next semester and submit
                                            your re-registration.
                                        </IntroText>
                                    )}
                                <PrimaryButton type="button" onClick={onStartReRegistration}>
                                    <span>{areAllStepsComplete ? "Review Re-Registration" : "Start Re-Registration"}</span>
                                    <i className="fal fa-chevron-right"></i>
                                </PrimaryButton>
                                </>
                            )}
                        </>
                    )}
                    {hasModuleSummary && (
                        <>
                        <Divider />
                        <ModuleSummaryContainer>
                            <ModuleSummaryHeader>
                                <ModuleSummaryTopRow>
                                    <ModuleSummaryTitleGroup>
                                        <ModuleSummaryTitle>Modules</ModuleSummaryTitle>
                                        <ModuleSummaryMeta>
                                            <i className="fal fa-book" style={{ color: "#467fcf" }}></i>
                                            <span>{deliveryMethodLabel || "Programme modules"}</span>
                                        </ModuleSummaryMeta>
                                    </ModuleSummaryTitleGroup>
                                    <ModuleSummaryAmount>{formatCurrency(modulesTotal)}</ModuleSummaryAmount>
                                </ModuleSummaryTopRow>
                                <ProgressTrackWrapper>
                                    <ProgressTrackBackground>
                                        <ProgressTrackFill
                                            $percent={progressPercent}
                                            $status={progressStatus}
                                        >
                                            <ProgressTrackLabel>
                                                {`${modulesCountSelected} of ${maxDisplayModules} Modules`}
                                            </ProgressTrackLabel>
                                        </ProgressTrackFill>
                                    </ProgressTrackBackground>
                                    <ProgressTrackHelper>
                                        {modulesRequiredMin > 0 || modulesAllowedMax > 0 ? (
                                            modulesRequiredMin > 0 && modulesAllowedMax > 0 ? (
                                                modulesRequiredMin === modulesAllowedMax ? (
                                                    `Exactly ${modulesRequiredMin} modules required`
                                                ) : (
                                                    `Min ${modulesRequiredMin}, max ${modulesAllowedMax} modules`
                                                )
                                            ) : modulesRequiredMin > 0 ? (
                                                `Minimum ${modulesRequiredMin} modules`
                                            ) : (
                                                `Maximum ${modulesAllowedMax} modules`
                                            )
                                        ) : (
                                            ""
                                        )}
                                    </ProgressTrackHelper>
                                </ProgressTrackWrapper>
                            </ModuleSummaryHeader>
                            <ProgressToggleRow
                                type="button"
                                onClick={() => setIsModuleSummaryExpanded(!isModuleSummaryExpanded)}
                            >
                                <span>{isModuleSummaryExpanded ? "Hide Detailed Summary" : "Show Detailed Summary"}</span>
                                <i
                                    className={`fal ${
                                        isModuleSummaryExpanded ? "fa-chevron-up" : "fa-chevron-down"
                                    }`}
                                ></i>
                            </ProgressToggleRow>
                            {isModuleSummaryExpanded && (
                                <ModuleItemsWrapper
                                    ref={moduleItemsWrapperRef}
                                    data-testid="pmc-module-items-wrapper"
                                >
                                        <ModuleItemsContainer>
                                            {items.length === 0 && !submitted && (
                                                <SummaryHelperText>
                                                    No modules selected yet. Once you select modules in the
                                                    Application tab, they will appear here.
                                                </SummaryHelperText>
                                            )}
                                            {items.map((item) => (
                                                <ModuleItemRow key={item.id}>
                                                    <ModuleItemName>{item.name}</ModuleItemName>
                                                    <ModuleItemPrice $isCredit={item.type === "credit"}>
                                                        {item.type === "credit"
                                                            ? `-${formatCurrency(item.price)}`
                                                            : formatCurrency(item.price)}
                                                    </ModuleItemPrice>
                                                </ModuleItemRow>
                                            ))}
                                        </ModuleItemsContainer>
                               
                                </ModuleItemsWrapper>
                            )}
                        </ModuleSummaryContainer>
                        </>)}
                </SidebarHeader>
            </SidebarScrollArea>
            {hasModuleSummary && (
                <SidebarFooterPinned
                    ref={sidebarFooterRef}
                    data-testid="pmc-sidebar-footer"
                >
                    <SummarySection>
                        {adjustmentsTotal !== 0 && (
                            <div>
                                <SummaryRow>
                                    <SummaryRowTitle>Academic Adjustments</SummaryRowTitle>
                                    <SummaryRowAmount $color="#2ab554">
                                        -{formatCurrency(adjustmentsTotal)}
                                    </SummaryRowAmount>
                                </SummaryRow>
                                <SummaryHelperText>
                                    Any fees paid for these modules will be credited to your account
                                    based on the modules you choose to re-register for.
                                </SummaryHelperText>
                            </div>
                        )}
                        <div>
                            
                            <SummaryRow>
                                <SummaryRowTitle>Deposit</SummaryRowTitle>
                                <SummaryRowAmount>{formatCurrency(calculatedDeposit)}</SummaryRowAmount>
                            </SummaryRow>
                            <SummaryHelperText $color="#c1232c">
                                The deposit must be paid to secure your spot for the upcoming
                                intake. Your student number{" "}
                                <strong>{studentNumber || "Your Student Number"}</strong> must be
                                used as the payment reference.
                            </SummaryHelperText>
                        </div>
                    </SummarySection>
          
                    <SummaryTotalRow>
                        <SummaryTotalLabel>Total</SummaryTotalLabel>
                            <SummaryTotalAmount>
                                {formatCurrency(apiTotalAmount > 0 && submitted ? apiTotalAmount : calculatedTotal)}
                                <TotalAsterisk>
                                    <i className="fa-solid fa-asterisk"/>
                                </TotalAsterisk>
                            </SummaryTotalAmount>
                    </SummaryTotalRow>
                    <SummaryDisclaimer>
                    <i className="fas fa-exclamation-circle"/>
                    <SummaryDisclaimerText>
                    Prices shown are an estimate and may not indicate the final price.
                    </SummaryDisclaimerText>
                       
                    </SummaryDisclaimer>
                </SidebarFooterPinned>
            )}
        </SidebarContainer>
        </>
    );
}

export default PMCReRegistrationSidebar;
